import { forwardRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FeatureToggle from '~/constants/featureToggle'
import {
  initializeChannelId,
  restoreCollaborationSession,
} from '~/store/duck/ckeditor'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  type EditorDocumentType,
  type FeatureElements,
  PuiCKEditorVariant,
} from '~/types'

import PuiCKEditorClassic, { type PuiCKEditorHandle } from '../PuiCKEditor'
import { isMedicalOrDischargeNoteType } from '../utils/CKEditorUtils'
import type { OnInitializeChannelId } from '../utils/useChannelId'
import { useGetEditorConfigByVariant } from '../utils/useGetEditorConfigByVariant'
import type { OnRestoreCollaborationSession } from '../utils/useOnEditorError'

export interface PuiCKEditorMedicalDischargeNotesProps {
  disabled: boolean
  editorReadyCallback?: () => void
  hideElements: FeatureElements[] | undefined
  id: string
  noteType: EditorDocumentType.SOAP_DISCHARGE | EditorDocumentType.SOAP_MEDICAL
  onChange: (value: any) => void
  soapId: string | undefined
}

const PuiCKEditorMedicalDischargeNotes = forwardRef<
  PuiCKEditorHandle,
  PuiCKEditorMedicalDischargeNotesProps
>(
  (
    {
      disabled,
      hideElements,
      id,
      noteType,
      onChange,
      soapId,
      editorReadyCallback,
    },
    ref,
  ) => {
    const dispatch = useDispatch()

    const { toolbar, removePlugins } = useGetEditorConfigByVariant(
      PuiCKEditorVariant.MEDICAL_OR_DISCHARGE,
    )

    const isAwoPmrRightRailEnabled = useSelector(
      getFeatureToggle(FeatureToggle.AWO_PMR_RIGHT_RAIL),
    )

    const computedToolbar = isAwoPmrRightRailEnabled
      ? toolbar.filter((item) => item !== 'comment')
      : toolbar

    const onInitializeChannelId = useCallback<OnInitializeChannelId>(
      (editorId, type, bundleType, restore) => {
        if (!soapId) {
          return
        }

        if (isMedicalOrDischargeNoteType(type)) {
          dispatch(
            initializeChannelId(
              type,
              { soapId },
              {
                bundleType,
                editorId,
                restore,
              },
            ),
          )
        }
      },
      [soapId],
    )

    const onRestoreCollaborationSession =
      useCallback<OnRestoreCollaborationSession>(
        (editorId, type, bundleType) => {
          if (!soapId) {
            return
          }

          if (isMedicalOrDischargeNoteType(type)) {
            dispatch(
              restoreCollaborationSession(
                type,
                { soapId },
                {
                  bundleType,
                  editorId,
                },
              ),
            )
          }
        },
        [soapId],
      )

    return (
      <PuiCKEditorClassic
        withRealTimeCollaboration
        disabled={disabled}
        editorHeight={380}
        editorReadyCallback={editorReadyCallback}
        hideElements={hideElements}
        id={id}
        noteType={noteType}
        ref={ref}
        removePlugins={removePlugins}
        toolbar={computedToolbar}
        onDangerousChange={onChange}
        onInitializeChannelId={onInitializeChannelId}
        onRestoreCollaborationSession={onRestoreCollaborationSession}
      />
    )
  },
)

export default PuiCKEditorMedicalDischargeNotes
